import { useState } from "react";
import { Loader2, LockKeyhole } from "lucide-react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@/components/common/GoogleTextField";
import Typography from "@mui/material/Typography";
import { toast } from "sonner";
import { resetPassword } from "@/api/auth";
import { STRONG_PASSWORD_REGEX } from "@/lib/validation";
import { FieldLabel } from "@/components/shared/FieldLabel";
import { useAuthStore } from "@/stores/auth";

interface ResetPasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ResetPasswordDialog({
  open,
  onClose,
}: ResetPasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [verifyPassword, setVerifyPassword] = useState("");
  const [matchError, setMatchError] = useState("");
  const [loading, setLoading] = useState(false);

  const strongError =
    newPassword.length > 0 && !STRONG_PASSWORD_REGEX.test(newPassword)
      ? "Password must be at least 8 characters with an uppercase letter, a number, and a special character."
      : "";

  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length > 0 &&
    verifyPassword.length > 0 &&
    !strongError &&
    !matchError &&
    !loading;

  const handleChange = (
    field: "currentPassword" | "newPassword" | "verifyPassword",
    value: string,
  ) => {
    if (field === "currentPassword") setCurrentPassword(value);
    if (field === "newPassword") {
      setNewPassword(value);
      if (verifyPassword && value !== verifyPassword)
        setMatchError("Passwords do not match");
      else setMatchError("");
    }
    if (field === "verifyPassword") {
      setVerifyPassword(value);
      if (newPassword && value !== newPassword)
        setMatchError("Passwords do not match");
      else setMatchError("");
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      await resetPassword({ currentPassword, newPassword, verifyPassword });
      toast.success("Password changed. Please sign in again.");
      handleClose();
      useAuthStore.getState().forceLogout();
      window.location.assign("/login");
    } catch {
      toast.error(
        "Failed to reset password. Please check your current password.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentPassword("");
    setNewPassword("");
    setVerifyPassword("");
    setMatchError("");
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LockKeyhole className="h-5 w-5 text-primary" />
        Reset Password
      </DialogTitle>
      <Typography variant="body2" color="text.secondary">
        Update the password used to sign in to your account
      </Typography>

      <DialogContent>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <FieldLabel htmlFor="rp-current">Old Password</FieldLabel>
            <TextField
              id="rp-current"
              size="small"
              fullWidth
              type="password"
              value={currentPassword}
              onChange={(e) => handleChange("currentPassword", e.target.value)}
              placeholder="Enter old password"
            />
          </div>

          <div className="space-y-2">
            <FieldLabel htmlFor="rp-new">New Password</FieldLabel>
            <TextField
              id="rp-new"
              size="small"
              fullWidth
              type="password"
              value={newPassword}
              onChange={(e) => handleChange("newPassword", e.target.value)}
              placeholder="New password"
              error={!!strongError}
            />
            {strongError && (
              <p className="text-xs text-destructive">{strongError}</p>
            )}
          </div>

          <div className="space-y-2">
            <FieldLabel htmlFor="rp-confirm">Confirm New Password</FieldLabel>
            <TextField
              id="rp-confirm"
              size="small"
              fullWidth
              type="password"
              value={verifyPassword}
              onChange={(e) => handleChange("verifyPassword", e.target.value)}
              placeholder="Confirm new password"
              error={!!matchError}
            />
            {matchError && (
              <p className="text-xs text-destructive">{matchError}</p>
            )}
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Resetting...
            </>
          ) : (
            "Reset Password"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
